import { ChatMessage, Organization, OrganizationMember } from '@prisma/client'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Toolbox } from '~/plugins/toolbox/shared'
import { useSdk } from '../../../hooks/useSdk'
import { useAttachments } from './useAttachments'

type Props = {
  conversationId: string
  messageParent?: ChatMessage
  organization: Organization
  organizationMember: OrganizationMember
  onSent: (chatMessage: ChatMessage) => void
  onSentOptimist: (chatMessage: ChatMessage) => void
}

export const useSendMessage = ({
  organization,
  conversationId,
  messageParent,
  organizationMember,
  onSent,
  onSentOptimist,
}: Props) => {
  const [isLoading, setLoading] = useState(false)
  const [content, setContent] = useState('')
  const [isHeaderOpen, setHeaderOpen] = useState(false)

  const sdk = useSdk({ organizationMember })

  const attachmentsHook = useAttachments({ organization })

  const send = async (content: string) => {
    if (Toolbox.Utility.isNull(content)) {
      return
    }

    const contentEnsured = content.trim()

    setLoading(true)

    try {
      const attachmentsUploaded = await attachmentsHook.uploadAttachments()

      const idTemporary = Toolbox.Utility.getUUID()

      const messageOptimist: ChatMessage = {
        id: idTemporary,
        idTemporary: idTemporary,
        content: contentEnsured,
        conversationId,
        parentId: messageParent?.id,
        createdAt: dayjs().toDate(),
        updatedAt: dayjs().toDate(),
        values: {},
        participantId: 'optimistic',
        deletedAt: undefined,
        status: 'SENT',
      }

      onSentOptimist(messageOptimist)

      sdk
        .sendMessage({
          content: contentEnsured,
          conversationId,
          idTemporary,
          parentId: messageParent?.id,
          attachments: attachmentsUploaded,
          canEmit: true,
        })
        .then(messageSent => onSent(messageSent))
        .catch(error => {
          console.error(`Could not send message: ${error.message}`)
        })

      setHeaderOpen(false)
    } catch (error) {
      console.error(`Could not send message: ${error.message}`)
    } finally {
      setContent('')
      setLoading(false)
    }
  }

  return {
    isLoading,
    isHeaderOpen,
    setHeaderOpen,
    content,
    setContent,
    attachments: attachmentsHook.attachments,
    setAttachments: attachmentsHook.setAttachments,
    send,
  }
}
